import { User, LogOut, FileText, RotateCcw, Tag } from 'lucide-react';
import { Dropdown, DropdownItem, DropdownDivider } from './ui';
import { UserMenuProps, MenuItemConfig } from '../types';
import { SanctumService } from '../services/sanctumService';
import { useUser } from '../contexts/UserContext';

/**
 * Get user initials from full name
 * @param fullName - User's full name
 * @returns Two-letter initials (e.g., "John Doe" -> "JD")
 */
function getUserInitials(fullName: string): string {
  if (!fullName) return 'U';

  const nameParts = fullName.trim().split(/\s+/);

  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }

  return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
}

export function UserMenu({ onShowPromos }: UserMenuProps) {
  const { user } = useUser();

  const menuItems: MenuItemConfig[] = [
    {
      type: 'item',
      icon: <User />,
      label: 'Account',
      onClick: () => { window.location.href = '/myaccount'; },
    },
    {
      type: 'item',
      icon: <FileText />,
      label: 'Orders',
      onClick: () => { window.location.href = '/myorders'; },
    },
    {
      type: 'item',
      icon: <Tag />,
      label: 'Active Promos',
      onClick: onShowPromos,
    },
    {
      type: 'divider',
    },
    ...(user?.switched ? [{
      type: 'item' as const,
      icon: <RotateCcw />,
      label: 'Switch Back',
      onClick: () => { window.location.href = '/switch-back'; },
    }] : []),
    {
      type: 'item',
      icon: <LogOut />,
      label: 'Logout',
      onClick: () => { SanctumService.logout(); },
      variant: 'danger',
    },
  ];

  const userInitials = user ? getUserInitials(user.fullName) : 'U';
  const userName = user?.fullName || 'User';

  const trigger = (
    <button
      className="px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center gap-2"
      aria-label={`User menu - ${userName}`}
      aria-haspopup="true"
      title={userName}
    >
      <div className="w-7 h-7 rounded-full bg-blue-800 flex items-center justify-center flex-shrink-0">
        <span className="text-white text-xs font-semibold" aria-hidden="true">
          {userInitials}
        </span>
      </div>
      <span className="text-white text-sm font-medium whitespace-nowrap">
        {userName}
      </span>
    </button>
  );

  return (
    <Dropdown trigger={trigger} align="right">
      {menuItems.map((item, index) => {
        if (item.type === 'divider') {
          return <DropdownDivider key={`divider-${index}`} />;
        }

        return (
          <DropdownItem
            key={item.label}
            icon={item.icon}
            onClick={item.onClick}
            variant={item.variant}
          >
            {item.label}
          </DropdownItem>
        );
      })}
    </Dropdown>
  );
}
